import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import ApiService from '../services/ApiService';
import { useWmsTerminalCommonStore } from './wmsTerminalCommonStore';
import type { Area, Location, Warehouse, Zone } from '../models/WarehouseModels';

import type { RefTag } from '../models/RefTagModels';
export const useTerminalContextStore = defineStore('terminalContextStore', () => {
  const commonStore = useWmsTerminalCommonStore();

  const warehouses = ref<Warehouse[]>(
    sessionStorage.getItem('wms.terminal-etiketten.warehouses') != null ? JSON.parse(sessionStorage['wms.terminal-etiketten.warehouses']) : []
  );
  const activeWarehouses = computed(() => warehouses.value.filter((warehouse) => warehouse.isActive !== false));

  const tags = ref<RefTag[]>([]);
  async function getAllWarehouses() {
    commonStore.setStatus({ ...commonStore.status, getWarehouseLoading: true, warehouseErrorMsg: '' });

    return ApiService.getAllWarehouses()
      .then((res) => {
        const data: Warehouse[] = res.data;
        warehouses.value = data;
        sessionStorage.setItem('wms.terminal-etiketten.warehouses', JSON.stringify(warehouses.value));
      })
      .catch((err) => {
        commonStore.status.warehouseErrorMsg = err.response.data.detail;
      })
      .finally(() => {
        commonStore.status.getWarehouseLoading = false;
        commonStore.setStatus(commonStore.status);
      });
  }

  const selectedWarehouseId = ref<number | null>(loadSelectedWarehouseId());
  async function getAllTags() {
    if (tags.value.length > 0) return;

    return ApiService.getAllTags()
      .then((res) => {
        tags.value = [...res.data].sort((a, b) => a.key.localeCompare(b.key, 'de'));
      })
      .catch(() => {
        tags.value = [];
      });
  }
  async function getAreasByWarehouse(warehouseId: number): Promise<Area[]> {
    const res = await ApiService.getAreasByWarehouse(warehouseId);
    return res.data ?? [];
  }

  async function getZonesByArea(areaId: number): Promise<Zone[]> {
    const res = await ApiService.getZonesByArea(areaId);
    return res.data ?? [];
  }

  async function getLocations(params: { warehouseId: number; areaId?: number | null; zoneId?: number | null; occupied?: boolean | null }): Promise<Location[]> {
    const res = await ApiService.getLocations(params);
    return res.data ?? [];
  }

  const lastMoveSourceWarehouseId = ref<number | null>(null);
  const selectedWarehouse = computed<Warehouse | null>(() => warehouses.value.find((warehouse) => warehouse.id === selectedWarehouseId.value) ?? null);

  function loadSelectedWarehouseId() {
    const storedValue = localStorage.getItem('wms.terminal-etiketten.currentWarehouse');
    if (storedValue == null || storedValue === '') {
      return null;
    }

    const parsedValue = Number(storedValue);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  function selectWarehouse(warehouseId: number) {
    selectedWarehouseId.value = warehouseId;
    localStorage.setItem('wms.terminal-etiketten.currentWarehouse', String(warehouseId));
  }

  function rememberMoveSourceWarehouse(warehouseId: number) {
    lastMoveSourceWarehouseId.value = warehouseId;
  }

  function clearWarehouse() {
    selectedWarehouseId.value = null;
    localStorage.removeItem('wms.terminal-etiketten.currentWarehouse');
  }

  return {
    activeWarehouses,
    tags,
    getAllTags,
    getAllWarehouses,

    selectedWarehouse,
    getAreasByWarehouse,
    getZonesByArea,
    getLocations,

    selectedWarehouseId,
    lastMoveSourceWarehouseId,
    selectWarehouse,
    rememberMoveSourceWarehouse,
    clearWarehouse,
  };
});
